import { z } from 'zod';
import { User, UserRole, Project } from '../types';
import { BROWSERS, REPORT_PLATFORM, USER_ROLE } from './constants';

export type NewPasswordForm = z.infer<typeof NewPasswordSchema>;
export type NewScreenshotForm = z.infer<typeof NewScreenshotSchema>;
export type ChangePasswordForm = z.infer<typeof ChangePasswordSchema>;
export type ForgotPassword = z.infer<typeof ForgotPasswordSchema>;
export type LoginForm = z.infer<typeof LoginSchema>;
export type NewReportForm = z.infer<typeof NewReportSchema>;
export type NewUserForm = z.infer<typeof NewUserSchema>;
export type NewProjectForm = z.infer<typeof NewProjectSchema>;
export type RefreshTokenForm = z.infer<typeof RefreshTokenSchema>;

const StringSchema = z.string({ required_error: 'error.required' });
const NotEmptyStringSchema = z
  .string({ required_error: 'error.required' })
  .trim()
  .min(1, 'error.required');
const EmailSchema = StringSchema.email('error.email');
const NewPasswordSchema = z.object({
  passwordNew: NotEmptyStringSchema.min(8).regex(
    /^(?=.*?[A-Z])(?=.*?[a-z])(?=.*?[0-9])(?=.*?[#?!@$%^&*-]).{8,}$/,
    'error.passwordRule',
  ),
  passwordConfirm: NotEmptyStringSchema.min(8),
});

export const RefreshTokenSchema = z.object({ refreshToken: StringSchema });

const NewPasswordCheck = (fields: NewPasswordForm, ctx: z.RefinementCtx) => {
  if (fields.passwordNew !== fields.passwordConfirm)
    ctx.addIssue({
      code: 'custom',
      path: ['passwordConfirm'],
      message: 'error.passwordMismatch',
    });
};

export const ForgotPasswordSchema = z.object({ email: EmailSchema });

export const ResetPasswordSchema = NewPasswordSchema.superRefine(NewPasswordCheck);

export const ChangePasswordSchema = NewPasswordSchema.extend({
  passwordOld: NotEmptyStringSchema.min(8),
}).superRefine(NewPasswordCheck);

export const LoginSchema = z.object({
  email: EmailSchema,
  password: StringSchema.min(8),
});

export const NewScreenshotSchema = z.object({ file: z.custom<File>() });

export const NewReportSchema = z
  .object({
    name: NotEmptyStringSchema,
    description: z.string().optional(),
    platform: NotEmptyStringSchema,
    env: NotEmptyStringSchema,
    currentDescription: NotEmptyStringSchema,
    reproduce: StringSchema.superRefine((v, ctx) => {
      const replaced = v.replaceAll(/(<([^>]+)>)/gi, ''); // remove tags
      if (!replaced.length) ctx.addIssue({ code: 'custom', message: 'error.required' });
      return v;
    }),
    currentScreenshots: z.custom<File>().array().optional(),
    expectedDescription: NotEmptyStringSchema,
    expectedScreenshots: z.custom<File>().array().optional(),
    priority: NotEmptyStringSchema,
    device: NotEmptyStringSchema.optional(),
    browsers: NotEmptyStringSchema.array().or(NotEmptyStringSchema).optional(),
    otherBrowsers: NotEmptyStringSchema.optional(),
    version: NotEmptyStringSchema.optional(),
  })
  .superRefine((data, ctx) => {
    if (data.platform === REPORT_PLATFORM.MOBILE && !data.device)
      ctx.addIssue({ code: 'custom', path: ['device'], message: 'error.required' });

    if (data.platform === REPORT_PLATFORM.MOBILE && !data.version)
      ctx.addIssue({ code: 'custom', path: ['version'], message: 'error.required' });

    if (data.platform === REPORT_PLATFORM.WEB && !data.browsers?.length)
      ctx.addIssue({ code: 'custom', path: ['version'], message: 'error.leastOne' });

    if (
      data.platform === REPORT_PLATFORM.WEB &&
      data.browsers?.includes(BROWSERS.OTHER) &&
      !data.otherBrowsers
    )
      ctx.addIssue({ code: 'custom', path: ['otherBrowser'], message: 'error.required' });
  });

export const NewUserSchema = z
  .object({
    language: NotEmptyStringSchema,
    email: EmailSchema,
    name: NotEmptyStringSchema,
    role: z.custom<UserRole>(),
    project: z.custom<Project>().array().min(1, 'error.required'),
  })
  .refine((fields) => fields.role === USER_ROLE.CLIENT);

export const NewProjectSchema = z.object({
  name: NotEmptyStringSchema,
  key: NotEmptyStringSchema,
  clients: z.custom<User>().array().min(1, 'error.required'),
});
